import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";

export default function MemberLogin() {
    const { t } = useT();
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!phone.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/member/lookup", { phone: phone.trim() });
            if (data.status === "pending") {
                toast(t("member.pending"));
                return;
            }
            if (data.status === "rejected") {
                toast.error(t("member.rejected"));
                return;
            }
            if (data.access_token) {
                localStorage.setItem("sc_member_token", data.access_token);
                localStorage.setItem("sc_member_id", data.member_id);
                navigate("/member/me", { replace: true });
            }
        } catch (err) {
            const code = err?.response?.status;
            if (code === 404) toast.error(t("member.not_found"));
            else if (code === 403) toast.error(t("member.denied"));
            else toast.error(err?.response?.data?.detail || "Lookup failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[100dvh] text-ink-primary relative" data-testid="member-login-page">
            <Header />
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(212,175,55,0.08),transparent_70%)]" />
            </div>
            <main className="relative max-w-md mx-auto px-6 pt-40 pb-24">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <div className="label-eyebrow">{t("member.eyebrow")}</div>
                    <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4">{t("member.title")}</h1>
                    <p className="text-ink-secondary mt-4">{t("member.subtitle")}</p>
                </motion.div>

                <form onSubmit={onSubmit} className="mt-12 space-y-6" data-testid="member-login-form">
                    <div>
                        <input
                            type="tel"
                            inputMode="tel"
                            data-testid="member-phone-input"
                            className="input-line"
                            placeholder={t("member.ph_phone")}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={busy || !phone.trim()}
                        data-testid="member-login-submit"
                        className="w-full btn-gold inline-flex items-center justify-center gap-3 px-10 py-4 rounded-sm font-medium tracking-wide disabled:opacity-40"
                    >
                        {busy ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                {t("member.looking")}
                            </>
                        ) : (
                            <>
                                <KeyRound size={18} />
                                {t("member.cta")}
                            </>
                        )}
                    </button>
                </form>
            </main>
        </div>
    );
}
